import React from "react";
import { styled } from "goober";

import TftColors from "@/game-tft/colors.mjs";
import useMatchListStats from "@/game-tft/use-matchlist-stats.mjs";

const Table = styled("div")`
  display: flex;
  flex-direction: row;

  .data-column {
    flex-grow: 1;
    margin-left: 19px;
  }

  .data-row {
    flex-grow: 1;
    margin-bottom: var(--sp-2_5);
    margin-right: 3px;
  }
`;

const Column = styled("div")`
  display: flex;
  flex-direction: column;
`;

const Row = styled("div")`
  display: block;
`;

const Container = styled("div")`
  margin-bottom: var(--sp-4);
  padding-inline: var(--sp-6);
`;
const Text = styled("div")`
  color: var(--shade3);
`;

export const getBarBackground = (x) => {
  switch (x) {
    case 1:
      return TftColors.placementDistribution.gradient[1];
    case 2:
      return TftColors.placementDistribution.gradient[2];
    case 3:
      return TftColors.placementDistribution.gradient[3];
    case 4:
      return TftColors.placementDistribution.gradient[4];
    default:
      return TftColors.placementDistribution.gradient.default;
  }
};

export const getXAxisColor = (x) => {
  switch (x) {
    case 1:
      return TftColors.placementDistribution.solid[1];
    case 2:
      return TftColors.placementDistribution.solid[2];
    case 3:
      return TftColors.placementDistribution.solid[3];
    case 4:
      return TftColors.placementDistribution.solid[4];
    default:
      return TftColors.placementDistribution.solid.default;
  }
};

const YAxis = ({ yMax, yInterval, isPercent }) => {
  const yAxisPoints = [];
  let y = yMax === 1 ? 0 : yMax % 2;
  while (y <= yMax) {
    yAxisPoints.push(
      <Text className="type-overline" key={`${y}-y-axis`}>
        {y + (isPercent ? "%" : "")}
      </Text>,
    );
    y += yInterval || 1;
  }
  return <YAxisContainer>{yAxisPoints}</YAxisContainer>;
};

const YAxisContainer = styled("div")`
  display: flex;
  flex-direction: column-reverse;
  justify-content: space-between;
  flex-grow: 1;
  margin-bottom: var(--sp-2_5);
`;

const XAxis = ({ xMin = 1, xMax, xInterval, getXAxisColor }) => {
  const xAxisPoints = [];
  let x = xMin;
  while (x <= xMax) {
    xAxisPoints.push(
      <Text
        key={`${x}-x-axis`}
        className="type-overline"
        style={{ color: getXAxisColor(x) }}
      >
        {x}
      </Text>,
    );
    x += xInterval;
  }
  return <XAxisContainer>{xAxisPoints}</XAxisContainer>;
};

const XAxisContainer = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex-grow: 1;
`;

const Line = styled("div")`
  width: var(--sp-1);
  border-radius: var(--br);
`;

const Graph = ({
  xMin = 1,
  xMax,
  yMax,
  xInterval,
  height,
  values,
  isPercent,
  getBarBackground,
}) => {
  const lines = [];
  let x = xMin;

  while (x <= xMax) {
    const barHeight = (height / yMax) * values[x];

    const line = (
      <Line
        key={`${x}-line`}
        style={{ height: `${barHeight}px`, background: getBarBackground(x) }}
        data-tip={values[x] + (isPercent ? "%" : "")}
      />
    );
    lines.push(line);
    x += xInterval;
  }
  return <GraphContainer>{lines}</GraphContainer>;
};

const GraphContainer = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
`;

export const BarGraph = (props) => {
  const {
    xMax,
    xInterval,
    yMax,
    yInterval,
    height,
    xMin,
    isPercent,
    getXAxisColor,
  } = props;

  return (
    <Table style={{ height: `${height + 10}px`, marginTop: "var(--sp-4)" }}>
      <Column>
        <YAxis
          yMax={yMax}
          yInterval={yInterval}
          height={height}
          isPercent={isPercent}
        />
      </Column>
      <Column className={"data-column"}>
        <Row className={"data-row"}>
          <Graph {...props} />
        </Row>
        <Row>
          <XAxis
            xMax={xMax}
            xMin={xMin}
            xInterval={xInterval}
            getXAxisColor={getXAxisColor}
          />
        </Row>
      </Column>
    </Table>
  );
};

const ProfileOverviewPlacementDistribution = ({ account }) => {
  const fullName = `${account?.account?.game_name}-${account?.account?.tag_line}`;
  const { placements } = useMatchListStats(fullName);

  const distribution = {};
  for (const p of placements) {
    if (typeof p !== "number") continue;
    if (!distribution[p]) {
      distribution[p] = 0;
    }
    distribution[p]++;
  }

  const yValues = Object.values(distribution);
  const yMax = Math.max(...yValues);
  const yInterval = Math.floor(yMax / 2);

  return (
    <Container>
      <BarGraph
        yMax={yMax}
        yInterval={yInterval}
        xMax={8}
        xInterval={1}
        values={distribution}
        height={100}
        getXAxisColor={getXAxisColor}
        getBarBackground={getBarBackground}
      />
    </Container>
  );
};

export default ProfileOverviewPlacementDistribution;
